import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { User, Building2, Save, X } from 'lucide-react';
import { DISTRITOS_PORTUGAL } from '../../types';
import { useForm } from '../../hooks/useForm';
import { useToast } from '../Toast';
import clientesService from '../../services/clientes';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
const ClienteForm = ({ cliente, onSave, onCancel, isLoading = false }) => {
    const { success, error: showError } = useToast();
    const [activeTab, setActiveTab] = useState(cliente?.tipoCliente || 'INDIVIDUAL');
    const initialValues = {
        tipoCliente: activeTab,
        nome: cliente?.nome || '',
        nomeComercial: cliente?.nomeComercial || '',
        nif: cliente?.nif || '',
        ccNumero: cliente?.ccNumero || '',
        ccValidade: cliente?.ccValidade ? cliente.ccValidade.split('T')[0] : '',
        email: cliente?.email || '',
        telefonePrincipal: cliente?.telefonePrincipal || '',
        telefoneSecundario: cliente?.telefoneSecundario || '',
        morada: cliente?.morada || '',
        codigoPostal: cliente?.codigoPostal || '',
        cidade: cliente?.cidade || '',
        distrito: cliente?.distrito || '',
        dataNascimento: cliente?.dataNascimento ? cliente.dataNascimento.split('T')[0] : '',
        genero: cliente?.genero || undefined,
        cae: cliente?.cae || '',
        conservatoria: cliente?.conservatoria || '',
        capitalSocial: cliente?.capitalSocial || undefined,
        notas: cliente?.notas || ''
    };
    const validationRules = {
        nome: {
            required: 'Nome é obrigatório',
            minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' },
            maxLength: { value: 255, message: 'Nome deve ter no máximo 255 caracteres' }
        },
        nif: {
            validate: (value) => {
                if (!value)
                    return true;
                return clientesService.validateNIF(value) || 'NIF inválido';
            }
        },
        ccNumero: {
            validate: (value) => {
                if (!value)
                    return true;
                return clientesService.validateCartaoCidadao(value) || 'Número do Cartão de Cidadão inválido';
            }
        },
        email: {
            pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Email inválido'
            }
        },
        telefonePrincipal: {
            validate: (value) => {
                if (!value)
                    return true;
                return clientesService.validateTelefone(value) || 'Telefone inválido (formato: +351XXXXXXXXX)';
            }
        },
        telefoneSecundario: {
            validate: (value) => {
                if (!value)
                    return true;
                return clientesService.validateTelefone(value) || 'Telefone inválido (formato: +351XXXXXXXXX)';
            }
        },
        codigoPostal: {
            validate: (value) => {
                if (!value)
                    return true;
                return clientesService.validateCodigoPostal(value) || 'Código postal inválido (formato: XXXX-XXX)';
            }
        },
        cae: {
            validate: (value) => {
                if (!value || activeTab !== 'EMPRESA')
                    return true;
                return /^\d{5}$/.test(value) || 'CAE deve ter 5 dígitos';
            }
        },
        capitalSocial: {
            validate: (value) => {
                if (!value || activeTab !== 'EMPRESA')
                    return true;
                return value > 0 || 'Capital social deve ser maior que 0';
            }
        }
    };
    const { values, errors, isSubmitting, handleChange, handleBlur, handleSubmit, setValue, getFieldProps } = useForm({
        initialValues,
        validationRules,
        onSubmit: async (formValues) => {
            try {
                // Limpar campos vazios antes de enviar
                const cleanedData = {
                    tipoCliente: activeTab,
                    nome: formValues.nome // Nome é sempre obrigatório
                };
                Object.keys(formValues).forEach((key) => {
                    const value = formValues[key];
                    // Só adicionar ao objeto se tiver valor (não vazio) e não for o nome (já adicionado)
                    if (key !== 'nome' && key !== 'tipoCliente' &&
                        value !== '' && value !== undefined && value !== null) {
                        cleanedData[key] = value;
                    }
                });
                const clienteData = cleanedData;
                let savedCliente;
                if (cliente) {
                    savedCliente = await clientesService.updateCliente(cliente.id, clienteData);
                    success('Cliente atualizado com sucesso!');
                }
                else {
                    savedCliente = await clientesService.createCliente(clienteData);
                    success('Cliente criado com sucesso!');
                }
                onSave(savedCliente);
            }
            catch (err) {
                // Tratamento específico de erros
                if (err.response?.data?.code === 'NIF_EXISTS') {
                    showError('NIF já cadastrado', 'Já existe um cliente com este NIF');
                }
                else if (err.response?.data?.code === 'EMAIL_EXISTS') {
                    showError('Email já cadastrado', 'Já existe um cliente com este email');
                }
                else if (err.response?.status === 409) {
                    showError('Dados duplicados', 'Cliente com informações já existentes');
                }
                else {
                    showError('Erro ao salvar cliente', err.response?.data?.message || err.message);
                }
            }
        }
    });
    // Atualizar tipo de cliente quando mudar tab
    useEffect(() => {
        setValue('tipoCliente', activeTab);
    }, [activeTab, setValue]);
    // Auto-formatação de campos
    const handleNIFChange = (e) => {
        const formatted = clientesService.formatNIF(e.target.value);
        setValue('nif', formatted);
    };
    const handleTelefoneChange = (field) => (e) => {
        const formatted = clientesService.formatTelefone(e.target.value);
        setValue(field, formatted);
    };
    const handleCodigoPostalChange = (e) => {
        const formatted = clientesService.formatCodigoPostal(e.target.value);
        setValue('codigoPostal', formatted);
    };
    const generoOptions = [
        { value: '', label: 'Selecione o género' },
        { value: 'MASCULINO', label: 'Masculino' },
        { value: 'FEMININO', label: 'Feminino' },
        { value: 'OUTRO', label: 'Outro' }
    ];
    const distritoOptions = [
        { value: '', label: 'Selecione o distrito' },
        ...DISTRITOS_PORTUGAL.map(distrito => ({
            value: distrito,
            label: distrito
        }))
    ];
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [_jsx("div", { className: "border-b border-gray-200", children: _jsxs("nav", { className: "-mb-px flex space-x-8", children: [_jsxs("button", { type: "button", onClick: () => setActiveTab('INDIVIDUAL'), className: `
              group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'INDIVIDUAL'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `, children: [_jsx(User, { className: "mr-2 h-4 w-4" }), "Pessoa Individual"] }), _jsxs("button", { type: "button", onClick: () => setActiveTab('EMPRESA'), className: `
              group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'EMPRESA'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `, children: [_jsx(Building2, { className: "mr-2 h-4 w-4" }), "Empresa"] })] }) }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(Input, { label: "Nome", placeholder: activeTab === 'INDIVIDUAL' ? 'Nome completo' : 'Razão social', required: true, ...getFieldProps('nome') }), activeTab === 'EMPRESA' && (_jsx(Input, { label: "Nome Comercial", placeholder: "Nome comercial da empresa", ...getFieldProps('nomeComercial') }))] }), _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-medium text-gray-900 mb-4", children: "Documenta\u00E7\u00E3o" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", children: [_jsx(Input, { label: "NIF", placeholder: "123456789", value: values.nif, onChange: handleNIFChange, onBlur: handleBlur, error: errors.nif, hint: "N\u00FAmero de Identifica\u00E7\u00E3o Fiscal", required: activeTab === 'EMPRESA' }), activeTab === 'INDIVIDUAL' && (_jsxs(_Fragment, { children: [_jsx(Input, { label: "Cart\u00E3o de Cidad\u00E3o", placeholder: "12345678 9 ZZ4", ...getFieldProps('ccNumero'), hint: "N\u00FAmero do Cart\u00E3o de Cidad\u00E3o" }), _jsx(Input, { label: "Validade CC", type: "date", ...getFieldProps('ccValidade'), hint: "Data de validade do CC" })] })), activeTab === 'EMPRESA' && (_jsxs(_Fragment, { children: [_jsx(Input, { label: "CAE", placeholder: "12345", ...getFieldProps('cae'), hint: "C\u00F3digo de Atividade Econ\u00F3mica" }), _jsx(Input, { label: "Conservat\u00F3ria", placeholder: "Nome da conservat\u00F3ria", ...getFieldProps('conservatoria') }), _jsx(Input, { label: "Capital Social", type: "number", placeholder: "0.00", step: "0.01", min: "0", ...getFieldProps('capitalSocial'), hint: "Valor em euros" })] }))] })] }), _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-medium text-gray-900 mb-4", children: "Contactos" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(Input, { label: "Email", type: "email", placeholder: "exemplo@email.com", ...getFieldProps('email') }), _jsx(Input, { label: "Telefone Principal", placeholder: "+351912345678", value: values.telefonePrincipal, onChange: handleTelefoneChange('telefonePrincipal'), onBlur: handleBlur, error: errors.telefonePrincipal }), _jsx(Input, { label: "Telefone Secund\u00E1rio", placeholder: "+351912345678", value: values.telefoneSecundario, onChange: handleTelefoneChange('telefoneSecundario'), onBlur: handleBlur, error: errors.telefoneSecundario })] })] }), _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-medium text-gray-900 mb-4", children: "Morada" }), _jsxs("div", { className: "space-y-4", children: [_jsx(Textarea, { label: "Morada", placeholder: "Rua, n\u00FAmero, andar, porta...", rows: 2, ...getFieldProps('morada') }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [_jsx(Input, { label: "C\u00F3digo Postal", placeholder: "1000-001", value: values.codigoPostal, onChange: handleCodigoPostalChange, onBlur: handleBlur, error: errors.codigoPostal }), _jsx(Input, { label: "Cidade", placeholder: "Lisboa", ...getFieldProps('cidade') }), _jsx(Select, { label: "Distrito", options: distritoOptions, ...getFieldProps('distrito') })] })] })] }), activeTab === 'INDIVIDUAL' && (_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-medium text-gray-900 mb-4", children: "Dados Pessoais" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(Input, { label: "Data de Nascimento", type: "date", ...getFieldProps('dataNascimento') }), _jsx(Select, { label: "G\u00E9nero", options: generoOptions, ...getFieldProps('genero') })] })] })), _jsx(Textarea, { label: "Notas", placeholder: "Observa\u00E7\u00F5es adicionais sobre o cliente...", rows: 3, ...getFieldProps('notas') }), _jsxs("div", { className: "flex justify-end space-x-3 pt-6 border-t border-gray-200", children: [_jsxs(Button, { type: "button", variant: "secondary", onClick: onCancel, disabled: isSubmitting || isLoading, children: [_jsx(X, { className: "w-4 h-4 mr-2" }), "Cancelar"] }), _jsx(Button, { type: "submit", variant: "primary", isLoading: isSubmitting || isLoading, leftIcon: _jsx(Save, { className: "w-4 h-4" }), children: cliente ? 'Atualizar Cliente' : 'Criar Cliente' })] })] }));
};
export default ClienteForm;
